import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { tableColumnPropType, tableResponsiveLayoutPropType } from '../utils/propTypes';
import InlineAlert from './InlineAlert';
const propTypes = {
  /**
   * Row grouping metadata
   */
  categories: PropTypes.arrayOf(PropTypes.object),
  /**
   * Column metadata
   */
  columns: PropTypes.arrayOf(tableColumnPropType).isRequired,
  /**
   * Array of data rows to display
   */
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * Footer metadata
   */
  footer: PropTypes.arrayOf(PropTypes.object),
  /**
   * layout metadata
   * columns: the number of columns in the layout,
   * key: name of the view. Used to map the colSpan properties in data,
   * maxWidth: the max window width that this view should be displayed at,
   * hideFooter: indicates if the footer should be displayed in this layout
   * separatorBeforeColumn: indicates where to put separators
   * cellClassNames: an object of dataKey: fn, where the fn returns a className for cells
   */
  responsiveView: tableResponsiveLayoutPropType.isRequired,
  /**
   * Table caption
   */
  tableCaption: PropTypes.string,
  /**
   * Object of theme-like props that will not change after the component mounts
   */
  theme: PropTypes.shape({
    /**
     * Dock the header to the top or the footer to the bottom when scrolling
     */
    fixedRows: PropTypes.oneOf(['header', 'footer', 'both', ''])
  })
};
const defaultProps = {
  theme: {
    fixedRows: ''
  }
};
class ResponsiveTable extends Component {
  constructor(props) {
    super(props);
    this.getCellWidth = (responsiveView, colSpan) => {
      const columnSizeMultiplier = colSpan && colSpan[responsiveView.key] ? colSpan[responsiveView.key] : 1;
      return columnSizeMultiplier * 100 / responsiveView.columns + '%';
    };
  }
  render() {
    const {
      columns,
      data,
      categories,
      tableCaption,
      responsiveView,
      footer,
      theme: {
        fixedRows
      }
    } = this.props;
    const isHeaderFixed = fixedRows === 'header' || fixedRows === 'both';
    const isFooterFixed = fixedRows === 'footer' || fixedRows === 'both';
    const footerClassNames = cx('asc-table-component-responsive-footer', {
      'asc-table-component-responsive-footer--fixed': isFooterFixed
    });
    const wrapperClassNames = cx('asc-table-component-wrapper', 'asc-table-component-responsive-wrapper', {
      'asc-table-component-scroll-y': fixedRows
    });
    const getRowMarkup = rows => {
      return rows.map((row, index) => {
        const hasInlineAlert = row.inlineAlert && row.inlineAlert.message;
        let rowClassNames = cx('asc-table-component-responsive-row', {
          'asc-table-component-row--odd': index % 2 === 1,
          'asc-table-component-row--even': index % 2 === 0,
          'asc-table-component-inline-alert': hasInlineAlert,
          [`asc-table-component-inline-alert--${row.inlineAlert?.type}`]: hasInlineAlert
        });
        return /*#__PURE__*/React.createElement("li", {
          className: rowClassNames,
          key: 'row-' + row.id
        }, hasInlineAlert && /*#__PURE__*/React.createElement("div", {
          className: "asc-table-component-responsive-cell asc-table-component-responsive-cell--inlineAlert"
        }, /*#__PURE__*/React.createElement(InlineAlert, {
          message: row.inlineAlert.message,
          type: row.inlineAlert.type,
          rowId: row.id
        })), columns.map(column => {
          let cellStyles = {
            minWidth: this.getCellWidth(responsiveView, column.colSpan)
          };
          const customCellClassFn = responsiveView.cellClassNames && responsiveView.cellClassNames[column.dataKey] ? responsiveView.cellClassNames[column.dataKey] : () => {};
          const cellClassNames = cx(`asc-table-component-responsive-cell asc-table-component-responsive-cell--${column.dataKey}`, customCellClassFn(row[column.dataKey], row.id));
          const headerClassNames = cx('asc-table-component-responsive-cell-label', {
            'asc-acc-hidden': column.hiddenHeader
          });
          return !column.hidden && /*#__PURE__*/React.createElement(React.Fragment, {
            key: 'cell-' + row.id + column.dataKey
          }, responsiveView.separatorBeforeColumn && responsiveView.separatorBeforeColumn.indexOf(column.dataKey) > -1 && /*#__PURE__*/React.createElement("hr", {
            className: "asc-table-component-responsive-line-break"
          }), /*#__PURE__*/React.createElement("div", {
            className: cellClassNames,
            style: cellStyles
          }, column.header && /*#__PURE__*/React.createElement("p", {
            className: headerClassNames
          }, column.header), /*#__PURE__*/React.createElement("div", {
            className: "asc-table-component-responsive-cell-content"
          }, column.cell ? column.cell(row[column.dataKey], row.id, row) : row[column.dataKey])));
        }));
      });
    };
    return /*#__PURE__*/React.createElement("figure", {
      className: wrapperClassNames
    }, tableCaption && /*#__PURE__*/React.createElement("figcaption", {
      className: "asc-acc-hidden"
    }, tableCaption), /*#__PURE__*/React.createElement("ul", {
      className: "asc-table-component asc-table-component-responsive"
    }, categories ? categories.map((category, categoryIndex) => {
      let categoryRows = data.filter(row => row.category === category.key);
      let categoryRowClassNames = cx('asc-table-component-responsive-category', {
        'asc-table-component-responsive-category--fixed': isHeaderFixed,
        ['asc-table-component-row--' + category.color]: category.color
      });
      return categoryRows.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, {
        key: category.key
      }, /*#__PURE__*/React.createElement("li", {
        className: categoryRowClassNames,
        style: {
          zIndex: categoryIndex + 1
        }
      }, /*#__PURE__*/React.createElement("div", null, category.label)), category.subCategories ? category.subCategories.map(subCategory => {
        let subCategoryRows = categoryRows.filter(row => row.subCategory === subCategory.key);
        let subCategoryRowClassNames = cx('asc-table-component-responsive-sub-category', {
          'asc-table-component-responsive-sub-category--fixed': isHeaderFixed,
          ['asc-table-component-row--' + subCategory.color]: subCategory.color
        });
        return subCategoryRows.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, {
          key: subCategory.key
        }, /*#__PURE__*/React.createElement("li", {
          className: subCategoryRowClassNames
        }, /*#__PURE__*/React.createElement("div", null, subCategory.label)), getRowMarkup(subCategoryRows));
      }) : getRowMarkup(categoryRows));
    }) : getRowMarkup(data)), footer && !responsiveView.hideFooter && /*#__PURE__*/React.createElement("div", {
      className: footerClassNames,
      style: {
        zIndex: categories ? categories.length + 2 : 'auto'
      }
    }, footer.filter(footerField => !footerField.empty).map((footerField, index) => {
      const footerFieldClassNames = cx('asc-table-component-responsive-footer-field', {
        'asc-table-component-responsive-footer-field-label': footerField.isLabel
      });
      return /*#__PURE__*/React.createElement("div", {
        className: footerFieldClassNames,
        key: 'footerField-' + index
      }, footerField.content);
    })));
  }
}
ResponsiveTable.propTypes = propTypes;
ResponsiveTable.defaultProps = defaultProps;
export default ResponsiveTable;